<template>
  <div
    :class="{ 'is-panel': layout === 'panel' }"
    v-infinite-scroll="loadMore"
    infinite-scroll-disabled="scrollDisabled"
    infinite-scroll-distance="10"
    infinite-scroll-throttle-delay="200"
  >
    <div class="search-page">
      <autocomplete
        :search="search"
        :default-value="$route.query.city"
        placeholder="Search for a city"
        aria-label="Search for a city"
        @submit="onSubmit"
        v-on:keyup.enter="handleEnter"
        class="w-full px-2 mt-4 mb-3"
      ></autocomplete>
      <!-- Phones in the panel layout: the four facets fold into a bottom
           sheet behind this button; the search field stays. -->
      <button
        v-if="facetsFolded"
        type="button"
        class="filters-btn"
        :aria-expanded="filtersOpen ? 'true' : 'false'"
        @click="filtersOpen = true"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"><path d="M3 5h14M6 10h8M8 15h4"/></svg>
        <span>Filters</span>
        <span v-if="activeFacetCount" class="filters-btn__count">{{ activeFacetCount }}</span>
      </button>
      <div
        v-if="facetsFolded && filtersOpen"
        class="filters-scrim"
        @click="filtersOpen = false"
      ></div>
      <div
        class="flex w-full my-4 sm:mt-auto flex-wrap"
        :class="{ 'filters-sheet': facetsFolded, 'is-open': filtersOpen }"
      >
        <div v-if="facetsFolded" class="filters-sheet__head">
          <span class="font-display text-lg">Filters</span>
          <button type="button" class="filters-sheet__done" @click="filtersOpen = false">Done</button>
        </div>
        <v-select
          class="text-lg text-gray-500 py-2 px-2 w-full md:w-1/4"
          aria-placeholder="Artists"
          placeholder="Artists"
          :select-on-key-codes="[188, 13]"
          select-on-tab
          v-model="artist"
          label="Artists"
          :options="artists"
        />
        <v-select
          class="text-lg text-gray-500 px-2 py-2 w-full md:w-1/4"
          aria-placeholder="Tags"
          placeholder="Tags"
          :select-on-key-codes="[188, 13]"
          select-on-tab
          v-model="tag"
          label="Tags"
          :options="tags"
        />
        <v-select
          class="text-lg text-gray-500 px-2 py-2 w-full md:w-1/4"
          aria-placeholder="Categories"
          placeholder="Categories"
          :select-on-key-codes="[188, 13]"
          select-on-tab
          v-model="category"
          label="Categories"
          :options="categories"
        />
        <v-select
          class="text-lg text-gray-500 px-2 py-2 w-full md:w-1/4"
          aria-placeholder="Commissioners"
          placeholder="Commissioners"
          :select-on-key-codes="[188, 13]"
          select-on-tab
          v-model="commissioner"
          label="Commissioners"
          :options="commissioners"
        />
        <button
          v-if="facetsFolded && activeFacetCount"
          type="button"
          class="filters-sheet__clear"
          @click="clearFacets"
        >Clear filters</button>
      </div>
    </div>
    <div class="search-info" v-if="!loading && !error">
      <div class="result" v-if="city">
        <div>{{ items.length }} results for</div>
        <div class="strong">
          <i>"{{ city }}"</i>
        </div>
      </div>
      <div class="text-sm">
        <!-- gap rather than per-element margins: the row keeps its spacing when
             a control is added, removed or wraps on a narrow screen. -->
        <div class="w-full flex flex-wrap items-center gap-x-6 gap-y-3">
          <div class="w-full sm:w-auto flex items-center gap-2">
            <span>SORT BY: </span>
            <select
              v-model="sort"
              class="select"
            >
              <option value="most-recent">Most Recent</option>
              <option value="location">By Location</option>
            </select>
          </div>
          <!-- Layout switcher, shown only when config.SHOW_LAYOUT_CONTROLS.
               Two layouts share the same data, map and filters; only the
               container changes. ?layout=current|panel, remembered per browser. -->
          <div v-if="showLayoutControls" class="w-full sm:w-auto flex items-center gap-2" role="radiogroup" aria-label="Layout">
            <span>Layout: </span>
            <div class="layout-switch">
              <button
                v-for="l in layouts"
                :key="l"
                type="button"
                role="radio"
                :aria-checked="layout === l ? 'true' : 'false'"
                :class="{ 'is-on': layout === l }"
                @click="layout = l"
              >{{ l }}</button>
            </div>
          </div>
          <div class="w-full sm:w-auto flex items-center gap-2" v-if="layout === 'current' || !narrow">
            <span>Show Map: </span>
            <span
              class="toggle-wrapper inline"
              role="checkbox"
              tabindex="0"
              @click="showMap = !showMap"
              :aria-checked="showMap ? 'true' : 'false'"
            >
              <span class="toggle-background" :style="backgroundStyles"></span>
              <span class="toggle-indicator" :style="indicatorStyles">
                <svg
                  v-if="showMap"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="#12302e"
                  viewBox="0 0 24 24"
                >
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path
                    d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"
                  />
                </svg>
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
    <div v-if="loading">
      <div class="loader">Loading...</div>
    </div>
    <div class="error" v-else-if="error">
      <i class="fa fa-exclamation-circle"></i>
      {{ error }}
    </div>
    <!-- mt-2 only: a bottom margin here is 8px of page scroll under a panel
         that already fills the viewport. -->
    <div class="w-full mt-2" v-else-if="layout === 'panel'">
      <div class="layout-panel" :class="{ 'is-map-view': mobileView === 'map', 'is-map-off': !narrow && !showMap }" ref="panel" :style="{ '--panel-top': panelTop + 'px' }">
        <div class="layout-panel__list">
          <div class="layout-panel__status">
            <span class="font-display text-base">{{ visibleItems.length }} {{ visibleItems.length === 1 ? 'artwork' : 'artworks' }}<span v-if="followingMap"> in view</span></span>
            <label class="layout-follow" v-if="mapOnScreen"><input type="checkbox" v-model="followMap" /> Update as the map moves</label>
          </div>
          <div class="layout-panel__grid" v-if="visibleItems.length">
            <ArtCard
              v-for="item in visibleItems.slice(0, listLimit)"
              :key="item.id"
              :item="item"
              :hot="hoveredId === item.id"
              @hover="hoveredId = $event.id"
              @leave="hoveredId = null"
            />
          </div>
          <!-- The empty state's v-else must sit right after the grid's v-if;
               anything in between breaks the chain and it renders always. -->
          <div class="layout-empty" v-else>
            <div class="font-display text-xl">Nothing here yet</div>
            <div class="text-sm text-gray-600">No artwork in this part of the map. Zoom out or clear a filter.</div>
          </div>
          <button
            v-if="visibleItems.length > listLimit"
            type="button"
            class="layout-more"
            @click="listLimit += 60"
          >Show more ({{ visibleItems.length - listLimit }} left)</button>
        </div>
        <div class="layout-panel__map" v-if="narrow || showMap">
          <MapComponent
            ref="mapComp"
            :items="mapItems"
            :center="center"
            :zoom="zoom"
            :highlighted-id="hoveredId"
            @art-click="redirect"
            @marker-hover="hoveredId = $event.id"
            @marker-leave="hoveredId = null"
            @bounds-change="bounds = $event"
          />
        </div>
        <!-- Phones: list or map, never both. The pill is the only control. -->
        <button
          v-if="narrow"
          type="button"
          class="layout-pill"
          @click="mobileView = mobileView === 'map' ? 'list' : 'map'"
        >{{ mobileView === 'map' ? 'List' : 'Map' }}</button>
      </div>
    </div>
    <div class="w-full my-2" v-else>
      <div class="grid-container" v-if="items.length > 0">
        <div
          :class="{ 'animate__animated animate__fadeInDown': showMap }"
          class=" map w-full"
          v-if="showMap"
        >
          <MapComponent
            ref="mapComp"
            :items="mapItems"
            :center="center"
            :zoom="zoom"
            @art-click="redirect"
          />
        </div>
        <!-- router-link, not a div with @click: it renders a real anchor, so the
             card is reachable by keyboard, opens in a new tab on middle click,
             and offers "copy link address" — while $router still handles the
             navigation client-side exactly as before. -->
        <router-link
          v-for="item in items"
          :key="item.id"
          :to="`/art/${item.friendly_id || item.id}`"
          :class="{ 'animate__animated animate__backInUp': !showMap }"
          class="w-full mt-2 px-1 block no-underline transform hover:scale-105 transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-red-600"
        >
          <div
            class="bg-white overflow-hidden border border-gray-200 h-full flex flex-col"
          >
            <!-- Image Section -->
            <div class="h-48 w-full bg-gray-200 relative flex-shrink-0">
              <img
                v-if="item && getPrimaryPhoto(item)"
                :src="getPrimaryPhoto(item).image_url"
                :alt="item.title"
                class="h-full w-full object-cover"
              />
              <div
                v-else
                class="h-full w-full flex items-center justify-center text-gray-500"
              >
                No Image
              </div>
            </div>

            <!-- Content Section -->
            <div class="p-4 text-left flex flex-col flex-grow">
              <h3 class="font-display text-xl mb-1 leading-tight text-gray-900">
                {{ item.title }}
              </h3>

              <!-- red-600 (#dc2626) rather than the site's #ff0000: at this size
                   the artist is normal text, and pure red scores 4.00:1 on white
                   where WCAG AA wants 4.5. red-600 reads as the same colour and
                   scores 4.83. The site gets away with #ff0000 at 18px. -->
              <div class="text-xs text-gray-500">
                by
                <span class="text-red-600 font-semibold">
                  {{
                    item.artists.length > 0
                      ? item.artists.map(a => a.name).join(', ')
                      : 'Unknown'
                  }}
                </span>
              </div>

              <!-- City and year are already in the map_data payload and were
                   simply not shown. With 44% of the collection carrying no
                   description, they are most of the context a card can give. -->
              <div v-if="item.city || item.year" class="text-xs text-gray-500 mt-1">
                {{ [item.city, item.year].filter(Boolean).join(' · ') }}
              </div>
            </div>
          </div>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import infiniteScroll from 'vue-infinite-scroll'
import artAroundApi from '../api'
import { getUniqueListBy } from '../features/utils'
import { isWizardToggle } from '../features/arts/wizard-route'
import { getCurrentPosition } from '../features/geolocation'
import MapComponent from '../components/MapComponent'
import ArtCard from '../components/ArtCard'
import { HOME_LAYOUT, SHOW_LAYOUT_CONTROLS } from '../config'
import { LAYOUTS, resolveLayout, withinBounds, boundsCenter, sortByDistanceTo } from '../features/map/layouts'

export default {
  directives: { infiniteScroll },
  components: {
    MapComponent,
    ArtCard,
  },
  data() {
    return {
      busy: false,
      // Removed map-specific data (art, popups, infoWindows) as they are in MapComponent
      page: 1,
      data: [],
      defaultLocation: {
        lat: 41.8781136,
        lng: -87.6297982,
      },
      cities: ['New York', 'Chicago'],
      sort: this.$route.query.sort || 'most-recent',
      artist: this.$route.query.artist || '',
      category: this.$route.query.category || '',
      tag: this.$route.query.tag || '',
      commissioner: this.$route.query.commissioner || '',
      artists: [],
      categories: [],
      tags: [],
      commissioners: [],
      items: [],
      allMapArts: [], // Full Global Cache Dataset
      loading: false,
      error: '',
      center: {},
      city: '',
      // Restored from the previous visit. Storage can throw outright -- Safari
      // in private mode, a browser set to block site data -- and returns null
      // when there is nothing stored, so both paths fall back to the old default.
      showMap: (() => {
        try {
          const stored = window.localStorage.getItem('artaround:showMap')
          // Nothing stored means a first visit, and the map has always been on
          // to begin with -- initialLoad forced it true on every load, which is
          // what this replaces.
          return stored === null ? true : stored === 'true'
        } catch (e) {
          // Storage can throw outright: Safari in private mode, a browser set
          // to block site data.
          return true
        }
      })(),
      userLocation: false,
      notMore: false,
      // Layout comparison (see the template). Same storage caveats as showMap.
      layouts: LAYOUTS,
      showLayoutControls: SHOW_LAYOUT_CONTROLS,
      layout: (() => {
        let stored = null
        try { stored = window.localStorage.getItem('artaround:layout') } catch (e) { /* see showMap */ }
        return resolveLayout(this.$route.query.layout, stored, {
          defaultLayout: HOME_LAYOUT,
          allowOverride: SHOW_LAYOUT_CONTROLS,
        })
      })(),
      hoveredId: null,
      bounds: null,
      followMap: true,
      // Distance from the viewport top to the panel, measured; the map and the
      // list take the rest of the viewport so the page itself never scrolls.
      panelTop: 360,
      listLimit: 60,
      // Below the lg breakpoint the panel becomes list-or-map (see the pill).
      narrow: false,
      mobileView: 'list',
      filtersOpen: false,
    }
  },
  async mounted() {
    window.scrollTo(0, 0)
    this.onResize = () => this.measurePanel()
    window.addEventListener('resize', this.onResize)
    this.mq = window.matchMedia('(max-width: 1023px)')
    this.narrow = this.mq.matches
    this.onMedia = e => { this.narrow = e.matches }
    if (this.mq.addEventListener) this.mq.addEventListener('change', this.onMedia)
    else this.mq.addListener(this.onMedia)

    artAroundApi.getAllCities().then(cities => {
      this.cities = [...new Set(cities)]
    })
    artAroundApi.getAllTags().then(tags => {
      this.tags = [...new Set(tags)]
    })
    artAroundApi.getAllArtists().then(artists => {
      this.artists = [...new Set(artists)]
    })
    artAroundApi.getAllCategories().then(categories => {
      this.categories = [...new Set(categories)]
    })
    artAroundApi.getAllCommissioners().then(commissioners => {
      this.commissioners = [...new Set(commissioners)]
    })

    // Load map data filters (Global once)
    artAroundApi.getMapArts({}).then(allArts => {
      this.allMapArts = allArts
    })

    this.fetchUserLocation()

    if (this.$route.query.sort && this.$route.query.sort === 'location') {
      try {
        const pos = await getCurrentPosition()
        const {
          coords: { latitude, longitude },
        } = pos
        this.defaultLocation = {
          lat: latitude,
          lng: longitude,
        }
        this.userLocation = true
        await this.initialLoad()
        this.updateSearchClasses()
      } catch (_) {
        this.sort = 'most-recent'
        await this.$router
          .push({
            path: '',
            query: {
              ...this.$route.query,
              sort: 'most-recent',
            },
          })
          .catch(err => {})
        await this.initialLoad()
        this.updateSearchClasses()
      }
    } else {
      await this.initialLoad()
      this.updateSearchClasses()
    }
  },
  watch: {
    showMap(value) {
      try {
        window.localStorage.setItem('artaround:showMap', String(value))
      } catch (e) {
        // Not worth surfacing: the toggle still works for this visit, it just
        // will not be remembered for the next one.
      }
      if (value) this.fitMap()
      this.bounds = null
    },
    // Frame the map on the art actually being shown. `zoom` above is only the
    // starting point: a city search used to open at a fixed z10, where all of
    // downtown collapses into two or three cluster bubbles.
    mapItems() {
      this.fitMap()
      this.listLimit = 60
    },
    mobileView() {
      this.measurePanel()
      this.fitMap()
    },
    loading(val) {
      if (!val) this.measurePanel()
    },
    layout(value) {
      try { window.localStorage.setItem('artaround:layout', value) } catch (e) { /* see showMap */ }
      this.hoveredId = null
      this.bounds = null
      this.fitMap()
      this.measurePanel()
    },
    center() {
      this.fitMap()
    },
    // MapComponent applies a changed `zoom` prop with setZoom(), which would
    // undo a fit that ran in the same flush; refitting after it keeps the
    // framing authoritative.
    zoom() {
      this.fitMap()
    },
    $route(to, from) {
      // This page also renders at /add, behind the upload wizard (router.js),
      // so opening or closing the wizard is a route change that reaches this
      // watcher. Nothing about the list changed -- the filters live in the
      // query and LayoutBase carries it across untouched -- and refetching
      // anyway would cost an API round-trip and recentre the map on the first
      // result, which the visitor watches happen behind the modal.
      if (isWizardToggle(to, from)) return

      // Map updates automatically via computed property 'mapItems'
      this.getArts(this.$route.query, 1, this.defaultLocation).then(
        ({ arts: arts, total: total }) => {
          this.notMore = false
          if (!total) {
            this.items = []
          }
          if (!arts || !arts.length) {
            this.notMore = true
          } else {
            this.items = [...arts]
            this.page = 1
            this.center = {
              lat: arts[0].lat || this.defaultLocation.lat,
              lng: arts[0].lng || this.defaultLocation.lng,
            }
          }
        }
      )
    },
    tag: {
      handler: function(value, _) {
        this.$router.push({
          path: '',
          query: {
            ...this.$route.query,
            sort: this.sort,
            category: this.category ? this.category : undefined,
            tag: this.tag ? this.tag : undefined,
            artist: this.artist ? this.artist : undefined,
            commissioner: this.commissioner ? this.commissioner : undefined,
          },
        })
      },
    },
    category: {
      handler: function(value, _) {
        this.$router.push({
          path: '',
          query: {
            ...this.$route.query,
            sort: this.sort,
            category: this.category ? this.category : undefined,
            tag: this.tag ? this.tag : undefined,
            artist: this.artist ? this.artist : undefined,
            commissioner: this.commissioner ? this.commissioner : undefined,
          },
        })
      },
    },
    artist: {
      handler: function(value, _) {
        this.$router.push({
          path: '',
          query: {
            ...this.$route.query,
            sort: this.sort,
            category: this.category ? this.category : undefined,
            tag: this.tag ? this.tag : undefined,
            artist: this.artist ? this.artist : undefined,
            commissioner: this.commissioner ? this.commissioner : undefined,
          },
        })
      },
    },
    commissioner: {
      handler: function(value, _) {
        this.$router.push({
          path: '',
          query: {
            ...this.$route.query,
            sort: this.sort,
            category: this.category ? this.category : undefined,
            tag: this.tag ? this.tag : undefined,
            artist: this.artist ? this.artist : undefined,
            commissioner: this.commissioner ? this.commissioner : undefined,
          },
        })
      },
    },
    items: {
      handler: function(value, _) {
        const lookup = this.items.reduce((a, e) => {
          a[e.id] = ++a[e.id] || 0
          return a
        }, {})

        const newItems = getUniqueListBy(this.items, 'id')
        if (newItems.length !== this.items.length) {
          this.items = [...newItems]
        }
      },
      deep: true,
    },
    sort: {
      handler: function(value, _) {
        if (value === 'location') {
          if (this.userLocation) {
            this.$router.push({
              path: '',
              query: {
                ...this.$route.query,
                sort: value,
                category: this.category ? this.category : undefined,
                tag: this.tag ? this.tag : undefined,
                artist: this.artist ? this.artist : undefined,
              },
            })
          } else {
            this.fetchUserLocation()
            alert(
              'ArtAround cannot access your current location check your permission settings'
            )
            this.sort = 'most-recent'
          }
        } else {
          this.$router
            .push({
              path: '',
              query: {
                ...this.$route.query,
                sort: value,
                category: this.category ? this.category : undefined,
                tag: this.tag ? this.tag : undefined,
                artist: this.artist ? this.artist : undefined,
              },
            })
            .catch(_ => {})
        }
      },
    },
  },
  computed: {
    activeFilters() {
      // Determine active filters from component state or route
      // Prioritize component state (v-models) as they drive the UI
      return {
        city: this.city || this.$route.query.city,
        artist: this.artist || this.$route.query.artist,
        tag: this.tag || this.$route.query.tag,
        category: this.category || this.$route.query.category,
        commissioner: this.commissioner || this.$route.query.commissioner,
        year: this.$route.query.year, // Year doesn't seem to have a v-model in template, rely on query
      }
    },
    // vue-infinite-scroll wants a property name, not an expression. Only the
    // current layout pages; the other two list what the map shows.
    scrollDisabled() {
      return this.busy || this.layout !== 'current'
    },
    // What the panel and map layouts list: the filtered set, narrowed to the
    // map's current bounds when following, nearest to the centre first.
    // On a phone the map is off screen in list view, and "in view" would
    // silently hide everything outside a map nobody is looking at.
    facetsFolded() {
      return this.narrow && this.layout === 'panel'
    },
    activeFacetCount() {
      const f = this.activeFilters
      return [f.artist, f.tag, f.category, f.commissioner].filter(Boolean).length
    },
    mapOnScreen() {
      return this.narrow ? this.mobileView === 'map' : this.showMap
    },
    followingMap() {
      return this.followMap && this.mapOnScreen
    },
    visibleItems() {
      const b = this.followingMap ? this.bounds : null
      const inView = this.mapItems.filter(a => withinBounds(a, b))
      return b ? sortByDistanceTo(inView, boundsCenter(b)) : inView
    },
    mapItems() {
      const filters = this.activeFilters

      return this.allMapArts.filter(art => {
        // City Filter (Case-insensitive partial)
        if (filters.city) {
          if (!art.city) return false
          const cityTerm = filters.city.toLowerCase()
          const artCity = art.city.toLowerCase()
          if (!artCity.includes(cityTerm)) return false
        }

        // Year Filter
        if (filters.year && art.year != filters.year) return false

        // Artist Filter (Case-insensitive)
        if (filters.artist) {
          const artistTerm = filters.artist.toLowerCase()
          const hasArtist =
            art.artists &&
            art.artists.some(a => a.name.toLowerCase() === artistTerm)
          if (!hasArtist) return false
        }

        // Commissioner Filter (Case-insensitive)
        if (filters.commissioner) {
          const commTerm = filters.commissioner.toLowerCase()
          const hasCommissioner =
            art.commissioners &&
            art.commissioners.some(c => c.name.toLowerCase() === commTerm)
          if (!hasCommissioner) return false
        }

        // Tag Filter (Case-insensitive)
        if (filters.tag) {
          const tagTerm = filters.tag.toLowerCase()
          const hasTag =
            art.tags && art.tags.some(t => t.toLowerCase() === tagTerm)
          if (!hasTag) return false
        }

        // Category Filter (Case-insensitive)
        if (filters.category) {
          const catTerm = filters.category.toLowerCase()
          const hasCategory =
            art.categories &&
            art.categories.some(c => c.toLowerCase() === catTerm)
          if (!hasCategory) return false
        }

        return true
      })
    },
    backgroundStyles() {
      return {
        // #12302e is the site's ink. The teal it replaced appears nowhere in the
        // brand, and reserving red for actions keeps a state toggle from reading
        // as something to click.
        backgroundColor: this.showMap ? '#12302e' : '#dddddd',
      }
    },
    indicatorStyles() {
      return {
        transform: this.showMap ? 'translateX(2rem)' : 'translateX(1rem)',
      }
    },
    zoom() {
      if (this.city && this.items.length) {
        return 10
      }
      return this.items.length ? 4 : 10
    },
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    if (this.mq) {
      if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.onMedia)
      else this.mq.removeListener(this.onMedia)
    }
  },
  methods: {
    clearFacets() {
      this.artist = ''
      this.tag = ''
      this.category = ''
      this.commissioner = ''
    },
    measurePanel() {
      this.$nextTick(() => {
        const el = this.$refs.panel
        if (!el) return
        const top = Math.round(el.getBoundingClientRect().top + window.scrollY)
        if (top > 0 && top !== this.panelTop) this.panelTop = top
      })
    },
    fitMap() {
      // The map is v-if'd on showMap and on having items, so it may not exist
      // yet when the data does; nextTick covers the render in between.
      this.$nextTick(() => {
        const map = this.$refs.mapComp
        if (!map) return
        // Filtered: frame the results. Unfiltered: the collection is worldwide
        // and framing all of it is a z1 globe, so open on Americas + Europe.
        const filtered = Object.values(this.activeFilters).some(Boolean)
        if (filtered && this.mapItems.length) map.fitBounds(this.mapItems)
        else map.fitHome()
      })
    },
    getPrimaryPhoto(art) {
      if (!art.photos || !art.photos.length) {
        return null
      }
      const mainPhoto = art.photos.find(photo => !!photo.primary)

      if (mainPhoto) {
        return mainPhoto
      }
      return art.photos[0]
    },
    // Removed clickOnArtMarker
    updateSearchClasses() {
      const resultList = document.querySelector('#autocomplete-result-list-1')
      resultList.style.zIndex = 10
    },
    loadMore: async function() {
      this.busy = true
      if (!this.loading && !this.notMore) {
        //const storedCity = localStorage.getItem("city");
        const { arts: arts, total: total } = await this.getArts(
          this.$route.query,
          this.page,
          this.defaultLocation
        )
        if (!total) {
          this.items = []
        }
        if (!arts || !arts.length) {
          this.notMore = true
        } else {
          // this.items.push(...arts)
          const newArray = this.items.filter(val => !arts.includes(val))
          this.items = [...newArray, ...arts]

          this.page++
        }
      }
      this.busy = false
    },
    fetchUserLocation() {
      const position = navigator.geolocation.getCurrentPosition(pos => {
        const {
          coords: { latitude, longitude },
        } = pos
        this.defaultLocation = {
          lat: latitude,
          lng: longitude,
        }
        this.userLocation = true
      })
    },
    redirect(id) {
      this.$router.push({
        path: `/art/${id}`,
      })
    },
    search(input) {
      if (!input) {
        return this.cities
      }

      return this.cities.filter(city =>
        city.toLowerCase().startsWith(input.toLowerCase())
      )
    },
    onSubmit(result) {
      if (result) {
        this.page = 1
        this.notMore = false
        this.city = result
        this.$router.push({
          path: '',
          query: {
            ...this.$route.query,
            sort: this.sort,
            city: result,
          },
        })
        //const storedCity = localStorage.getItem("city");
        //this.getRequest(result, this.colors, this.decades, this.places, true);
      }
    },
    handleEnter(evt) {
      const {
        target: { value },
      } = evt
      this.notMore = false
      this.city = value
      this.$router.push({
        path: '/',
        query: {
          ...this.$route.query,
          sort: this.sort,
          city: value,
        },
      })

      // this.getRequest(value, this.colors, this.decades, this.places, true);
    },
    async getCurrentPosition(city) {
      try {
        const { data: center } = await axios.get(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${city ||
            'USA'}&key=AIzaSyCVG5JC8HYfgnx1WipC9Mjgs2HL09IG48E`
        )
        return center &&
          center.results &&
          center.results.length &&
          center.results[0] &&
          center.results[0].geometry
          ? center.results[0].geometry.location
          : { lat: 0, lng: 0 }
      } catch (_e) {
        return { lat: 0, lng: 0 }
      }
    },
    assignLatLng(defaultPosition) {
      return function(item) {
        return {
          ...item,
          lat: item.lat || defaultPosition.lat,
          lng: item.lng || defaultPosition.lng,
        }
      }
    },
    async getArts(params, page, defaultPosition) {
      try {
        const { year, artist, tag, category, city, sort, commissioner } = params
        if (year) {
          const {
            arts = [],
            total = 0,
          } = await artAroundApi.getArtsByYear(year, { page: page })
          return {
            arts: arts.map(this.assignLatLng(defaultPosition)),
            total: total,
          }
        } else {
          const { arts, total } = await artAroundApi.getArts({
            page: page,
            city: city,
            artist: artist,
            tag: tag,
            category: category,
            sort: sort,
            lat: this.userLocation ? this.defaultLocation.lat : 0,
            lng: this.userLocation ? this.defaultLocation.lng : 0,
            commissioner: commissioner,
          })
          return {
            arts: arts.map(this.assignLatLng(defaultPosition)),
            total: total,
          }
        }
      } catch (e) {
        console.error(e)
        return { arts: [], total: 0 }
      }
    },
    async initialLoad() {
      // Pre loading
      this.page = 1
      this.loading = true
      const { year, artist, tag, category, city } = this.$route.query
      try {
        const defaultPosition = this.getCurrentPosition()

        const { arts: arts, total: _ } = await this.getArts(
          { ...this.$route.query, sort: this.sort },
          1,
          defaultPosition
        )

        this.items = arts

        this.center =
          arts && arts.length
            ? { lat: arts[0].lat, lng: arts[0].lng }
            : this.defaultLocation
        this.page++
        this.city = city
      } catch (e) {
        console.error(e)
        this.items = []
        this.center = this.defaultLocation
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.search-page {
  width: 100%;
  /* #f2f2f2 is the surface grey artaround.org uses; #f8f8f8 was close but not it. */
  background: #f2f2f2;
  display: flex;
  flex-direction: column;
  /* The band had no padding of its own, so the controls sat flush against the
     edge of the grey. align-items was `center`, which is what made the search
     field float at half width in the middle of the row while the four filters
     below it spanned the full width -- the two never lined up with each other. */
  align-items: stretch;
  padding: 4px 14px 14px;
}

.select {
  /* Was borderless with a -15px margin hack in the template to sit next to its
     label. It now carries the same border, height and radius-free shape as the
     search field and the four filters, so the row reads as one set of controls. */
  border: 1px solid #dddddd;
  background-color: #ffffff;
  font-size: 1rem;
  font-weight: 600;
  color: #12302e;
  -webkit-appearance: none;
  -moz-appearance: none;
  background: none;
  background-image: url("data:image/svg+xml;utf8,<svg fill='%2312302e' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/><path d='M0 0h24v24H0z' fill='none'/></svg>");
  background-repeat: no-repeat;
  background-position-x: 100%;
  background-position-y: 8px;
  padding: 0 2.25rem 0 0.75rem;
  height: 44px;
  cursor: pointer;
}

.select:focus {
  outline: none;
  border-color: #dc2626;
  box-shadow: 0 0 0 1px #dc2626;
}

/* The bar held three control heights (50 / 44 / 37) and three border colours:
   the search field's border matched its own background so it read as a filled
   box, the sort select had its own, and vue-select shipped its default. One
   shape for all six, ::v-deep because vue-select renders its own DOM. */
.search-page ::v-deep .autocomplete input,
.search-page ::v-deep .vs__dropdown-toggle {
  height: 44px;
  background-color: #ffffff;
  border: 1px solid #dddddd;
  border-radius: 0;
}

/* vue-select puts its own <input> inside the toggle. A bare `input` selector
   above gave that one a border and a white fill too, so each filter rendered a
   box inside a box with the caret stranded between them. It stays bare; the
   toggle is the control. */
.search-page ::v-deep .vs__search,
.search-page ::v-deep .vs__search:focus {
  border: 0;
  background: transparent;
  margin: 0;
  padding: 0 4px;
  height: auto;
}

/* An outline PLUS a recoloured border drew two concentric lines. The border
   goes red and a 1px shadow of the same colour thickens it, so focus reads as
   one heavier edge in the place the edge already was. */
.search-page ::v-deep .autocomplete input:focus,
.search-page ::v-deep .vs--open .vs__dropdown-toggle {
  outline: none;
  border-color: #dc2626;
  box-shadow: 0 0 0 1px #dc2626;
}

/* The open menu still wore vue-select's defaults: a #5897fb highlight and a
   translucent grey border, neither of which appears anywhere else on the page.
   It now matches the control it drops out of. */
.search-page ::v-deep .vs__dropdown-menu {
  border: 1px solid #dc2626;
  border-top: 0;
  border-radius: 0;
  box-shadow: none;
}

.search-page ::v-deep .vs__dropdown-option {
  color: #12302e;
  padding: 10px 12px;
}

.search-page ::v-deep .vs__dropdown-option--highlight {
  background: #12302e;
  color: #ffffff;
}

.search-page ::v-deep .vs__search::placeholder,
.search-page ::v-deep input::placeholder {
  color: #5b5e5e;
}

.search-info {
  /* Was width:95% with padding:0 2.5% -- percentages that landed 55px short of
     the band above it at this viewport and drifted at every other one. Same
     inset as .search-page instead, so the two rows share an edge. */
  font-size: 1.2rem;
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 0 22px;
}

.result {
  margin-top: 10px;
  font-weight: 300;
  display: flex;
}

.strong {
  margin-left: 5px;
  font-weight: bold;
}

.toggle-wrapper {
  display: inline-block;
  position: relative;
  cursor: pointer;
  height: 2rem;
  width: 3rem;
  border-radius: 9999px;
}

/* Was `outline: 0` on a control with tabindex="0" and role="checkbox": operable
   by keyboard, invisible while you did it. */
.toggle-wrapper:focus {
  outline: 2px solid #dc2626;
  outline-offset: 2px;
}

.toggle-background {
  display: inline-block;
  border-radius: 9999px;
  height: 100%;
  width: 100%;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: background-color 0.2s ease;
}

.toggle-indicator {
  display: flex;
  font-size: 0.8rem;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0.25rem;
  left: -0.75rem;
  height: 1.5rem;
  width: 1.5rem;
  background-color: #fff;
  border-radius: 9999px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
}

.map {
  grid-area: map;
  height: 26rem;
}

/* ---- layout comparison ------------------------------------------------ */

/* Panel layout: the whole results page steps out of LayoutBase's container to
   the viewport's edges, and the filter band, the sort row and the panel share
   ONE gutter -- a full-bleed panel under a contained filter bar would bring
   back the five-different-edges problem the bar was cleaned of. overflow
   hidden so map tiles past the edge never widen the document. */
.is-panel {
  width: 100vw;
  position: relative;
  left: 50%;
  margin-left: -50vw;
  overflow: hidden;
}
.is-panel .search-page {
  padding-left: 1rem;
  padding-right: 1rem;
}
/* The band's controls carry 8px of their own (px-2), so the rows without
   controls take the gutter plus those 8px: every visible edge lands on one x. */
.is-panel .search-info {
  padding: 0 1.5rem;
}
.is-panel .layout-panel {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}
@media (min-width: 1024px) {
  .is-panel .search-page {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
  .is-panel .search-info {
    padding-left: 2rem;
    padding-right: 2rem;
  }
  .is-panel .layout-panel {
    padding: 0 0 0 2rem;
  }
}
.layout-switch {
  display: inline-flex;
  border: 1px solid #dddddd;
}
.layout-switch button {
  height: 2.25rem;
  padding: 0 0.75rem;
  font-size: 0.8125rem;
  text-transform: capitalize;
  color: #5b5e5e;
  background: #fff;
}
.layout-switch button + button {
  border-left: 1px solid #dddddd;
}
.layout-switch button.is-on {
  background: #12302e;
  color: #fff;
}
.layout-switch button:focus {
  outline: 2px solid #dc2626;
  outline-offset: -2px;
}
.layout-follow {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: #5b5e5e;
  cursor: pointer;
}
.layout-follow input {
  accent-color: #12302e;
  margin: 0;
}
.layout-empty {
  padding: 3rem 1.5rem;
  border: 1px dashed #dddddd;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
}
.layout-empty--strip {
  padding: 1rem;
  font-size: 0.875rem;
  color: #5b5e5e;
}

/* Option 0: results panel that scrolls beside a sticky map.
   Phones (below lg): list OR map, switched by the floating pill. The map stays
   mounted while hidden so its bounds and the hover sync survive the switch;
   MapComponent's ResizeObserver re-measures it when it comes back. */
.layout-panel {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
  padding: 0 1rem 5rem;
  box-sizing: border-box;
}
.layout-panel__map {
  display: none;
}
.layout-panel.is-map-view .layout-panel__list {
  display: none;
}
/* Phone map view: the whole screen, over the header and the filters; the
   pill is the way back. */
.layout-panel.is-map-view .layout-panel__map {
  display: block;
  position: fixed;
  inset: 0;
  z-index: 1050;
  height: 100vh;
}
.layout-panel.is-map-view {
  padding-bottom: 0;
}
.layout-pill {
  position: fixed;
  left: 50%;
  bottom: 3rem; /* clear of the map's attribution strip */
  transform: translateX(-50%);
  z-index: 1100;
  height: 44px;
  padding: 0 1.25rem;
  border: 1px solid #12302e;
  background: #12302e;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 600;
  box-shadow: 0 8px 24px rgba(18, 48, 46, 0.25);
}
.layout-pill:focus {
  outline: 2px solid #dc2626;
  outline-offset: 2px;
}
/* Facets folded into a sheet (phones, panel layout) */
.filters-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 44px;
  margin-top: 0.75rem;
  padding: 0 0.875rem;
  border: 1px solid #dddddd;
  background: #fff;
  color: #12302e;
  font-size: 0.875rem;
  font-weight: 600;
  align-self: flex-start;
}
.filters-btn:focus,
.filters-sheet__done:focus,
.filters-sheet__clear:focus {
  outline: 2px solid #dc2626;
  outline-offset: 2px;
}
.filters-btn__count {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: #12302e;
  color: #fff;
  font-size: 0.75rem;
  line-height: 20px;
  text-align: center;
}
.filters-scrim {
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(18, 48, 46, 0.45);
}
.filters-sheet {
  display: none;
}
.filters-sheet.is-open {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1201;
  margin: 0;
  padding: 0.75rem 0 1rem;
  background: #fff;
  border-top: 1px solid #dddddd;
  box-shadow: 0 -8px 24px rgba(18, 48, 46, 0.18);
  max-height: 85vh;
  overflow: auto;
}
.filters-sheet__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.5rem 0.5rem;
}
.filters-sheet__done {
  height: 40px;
  padding: 0 1rem;
  background: #12302e;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
}
.filters-sheet__clear {
  height: 44px;
  margin: 0.5rem 0.5rem 0;
  border: 1px solid #12302e;
  background: #fff;
  color: #12302e;
  font-size: 0.875rem;
  font-weight: 600;
}

/* Same family as the site's buttons: the display face, uppercase, square,
   ink instead of red because red is reserved for "Upload art". */
.layout-more {
  flex: none; /* a column-flex child with a max-height parent would squash it */
  width: 100%;
  height: 3.25rem;
  margin-top: 1rem;
  border: 2px solid #12302e;
  background: #fff;
  color: #12302e;
  font-family: "Roboto Condensed", "Arial Narrow", Arial, sans-serif;
  font-size: 1.0625rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.layout-more:hover {
  background: #12302e;
  color: #fff;
}
.layout-more:focus {
  outline: 2px solid #dc2626;
  outline-offset: 2px;
}
.layout-panel__list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.layout-panel__status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.layout-panel__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}
@media (min-width: 1024px) {
  .layout-panel {
    grid-template-columns: 35rem minmax(0, 1fr);
    align-items: start;
    /* The list keeps the page gutter; the map runs to the viewport's edge. */
    padding: 0 0 0 1.5rem;
  }
  /* Header + filter band + sort row + this = 100vh. --panel-top is measured
     by measurePanel(); the fallback only covers the first paint. */
  .layout-panel__list {
    max-height: calc(100vh - var(--panel-top, 22rem));
    overflow: auto;
    padding-right: 0.5rem;
  }
  .layout-panel__map,
  .layout-panel.is-map-view .layout-panel__map {
    display: block;
    position: sticky;
    top: 0;
    height: calc(100vh - var(--panel-top, 22rem));
  }
  .layout-panel.is-map-view .layout-panel__list {
    display: flex;
  }
  /* Show Map off: the list takes the whole width, like the current layout's grid. */
  .layout-panel.is-map-off {
    grid-template-columns: minmax(0, 1fr);
    padding-right: 2rem;
  }
  .layout-panel.is-map-off .layout-panel__list {
    max-height: none;
    overflow: visible;
  }
  .layout-panel.is-map-off .layout-panel__grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}



.grid-container {
  display: grid;
  grid-template-areas:
    'map'
    '.';
  grid-gap: 10px;
  grid-template-rows: auto;
  grid-template-columns: repeat(1, 1fr);
}

@media (min-width: 640px) {
  .map {
    height: 38rem;
  }

  .grid-container {
    grid-template-areas:
      'map map'
      '. .';
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 768px) {
  .grid-container {
    grid-template-areas:
      'map map .'
      'map map .'
      '. . .';
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 1024px) {
  .grid-container {
    grid-template-areas:
      'map map map .'
      'map map map .'
      '.   .   .   .';
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
